import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Page, Layouts, useNotification } from '@strapi/strapi/admin';
import {
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Td,
  Th,
  Typography,
  IconButton,
  Flex,
  EmptyStateLayout,
  Dialog,
} from '@strapi/design-system';
import { Plus, Pencil, Trash, Mail } from '@strapi/icons';

import { useTemplates } from '../hooks/useTemplates';
import { useTemplatesApi, type EmailTemplateListItem } from '../api/templates';
import { PLUGIN_ID } from '../pluginId';

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const HomePage: React.FC = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const api = useTemplatesApi();
  const { toggleNotification } = useNotification();
  const { items, loading, refresh } = useTemplates();

  const [pendingDelete, setPendingDelete] = React.useState<EmailTemplateListItem | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const startCreate = () => navigate('library');

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.remove(pendingDelete.documentId);
      setPendingDelete(null);
      await refresh();
      toggleNotification({ type: 'success', message: 'Template deleted' });
    } catch (err: any) {
      toggleNotification({ type: 'danger', message: err?.message ?? 'Delete failed' });
    } finally {
      setDeleting(false);
    }
  };

  const title = formatMessage({ id: `${PLUGIN_ID}.list.title`, defaultMessage: 'Email Templates' });
  const subtitle = formatMessage({
    id: `${PLUGIN_ID}.list.subtitle`,
    defaultMessage: 'Design responsive emails and send them with dynamic data.',
  });

  if (loading) {
    return <Page.Loading />;
  }

  return (
    <Page.Main>
      <Page.Title>{title}</Page.Title>
      <Layouts.Header
        title={title}
        subtitle={subtitle}
        primaryAction={
          <Button startIcon={<Plus />} onClick={startCreate}>
            {formatMessage({ id: `${PLUGIN_ID}.list.create`, defaultMessage: 'Create new template' })}
          </Button>
        }
      />

      <Layouts.Content>
        {items.length === 0 ? (
          <EmptyStateLayout
            icon={<Mail width="10rem" />}
            content={formatMessage({
              id: `${PLUGIN_ID}.list.empty.description`,
              defaultMessage: 'Click "Create new template" to design your first email.',
            })}
            action={
              <Button variant="secondary" startIcon={<Plus />} onClick={startCreate}>
                {formatMessage({ id: `${PLUGIN_ID}.list.create`, defaultMessage: 'Create new template' })}
              </Button>
            }
          />
        ) : (
          <Table colCount={4} rowCount={items.length}>
            <Thead>
              <Tr>
                <Th>
                  <Typography variant="sigma">
                    {formatMessage({ id: `${PLUGIN_ID}.list.column.name`, defaultMessage: 'Name' })}
                  </Typography>
                </Th>
                <Th>
                  <Typography variant="sigma">
                    {formatMessage({ id: `${PLUGIN_ID}.list.column.subject`, defaultMessage: 'Subject' })}
                  </Typography>
                </Th>
                <Th>
                  <Typography variant="sigma">
                    {formatMessage({ id: `${PLUGIN_ID}.list.column.updatedAt`, defaultMessage: 'Last updated' })}
                  </Typography>
                </Th>
                <Th>
                  <Typography variant="sigma">
                    {formatMessage({ id: `${PLUGIN_ID}.list.column.actions`, defaultMessage: 'Actions' })}
                  </Typography>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((row) => (
                <Tr key={row.documentId} onClick={() => navigate(row.documentId)} style={{ cursor: 'pointer' }}>
                  <Td>
                    <Typography textColor="neutral800" fontWeight="semiBold">{row.name}</Typography>
                  </Td>
                  <Td>
                    <Typography textColor="neutral800">{row.subject}</Typography>
                  </Td>
                  <Td>
                    <Typography textColor="neutral600">{formatDate(row.updatedAt)}</Typography>
                  </Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <Flex gap={1}>
                      <IconButton
                        label={formatMessage({ id: `${PLUGIN_ID}.list.action.edit`, defaultMessage: 'Edit' })}
                        onClick={() => navigate(row.documentId)}
                      >
                        <Pencil />
                      </IconButton>
                      <IconButton
                        label={formatMessage({ id: `${PLUGIN_ID}.list.action.delete`, defaultMessage: 'Delete' })}
                        onClick={() => setPendingDelete(row)}
                      >
                        <Trash />
                      </IconButton>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Layouts.Content>

      <Dialog.Root open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <Dialog.Content>
          <Dialog.Header>
            {formatMessage({ id: `${PLUGIN_ID}.list.delete.confirm.title`, defaultMessage: 'Delete this template?' })}
          </Dialog.Header>
          <Dialog.Body>
            {formatMessage({ id: `${PLUGIN_ID}.list.delete.confirm.body`, defaultMessage: 'This action cannot be undone.' })}
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Cancel>
              <Button fullWidth variant="tertiary">
                {formatMessage({ id: `${PLUGIN_ID}.list.delete.confirm.cancel`, defaultMessage: 'Cancel' })}
              </Button>
            </Dialog.Cancel>
            <Button
              fullWidth
              variant="danger-light"
              startIcon={<Trash />}
              onClick={handleDelete}
              loading={deleting}
            >
              {formatMessage({ id: `${PLUGIN_ID}.list.delete.confirm.confirm`, defaultMessage: 'Delete' })}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>

    </Page.Main>
  );
};

export default HomePage;
